import {type RefObject, useEffect, useRef} from "react";
import {formatDate, isSameDay, momentToPlainDate, parseDateInput} from "@/shared/utils/datefunctions";
import {Temporal} from "@js-temporal/polyfill";

export type LegacySingleDatePickerDrops = "down" | "up" | "auto";

interface UseLegacySingleDatePickerOptions {
	inputRef: RefObject<HTMLInputElement>;
	value: Temporal.PlainDate | null;
	onValueChange: (value: Temporal.PlainDate | null) => void;
	drops?: LegacySingleDatePickerDrops;
}

function hasLegacyDateRangePickerSupport() {
	return typeof globals !== "undefined" && typeof $.fn.daterangepicker === "function";
}

/**
 * Bridges a controlled React input to the legacy jQuery daterangepicker single-date behavior.
 *
 * The component's public value is a {@link Temporal.PlainDate} (the canonical date type), not a
 * formatted string: the widget's own native representation is already a moment, so speaking moment
 * at the boundary removes the parse-on-sync / format-on-apply round-trip the string API forced.
 * The {@link globals.dateFormat} string lives only inside this bridge — it is the input element's
 * display text, derived from the moment when syncing and discarded (re-parsed back into the picker's
 * moment) on the way out.
 */
export function useLegacySingleDatePicker({
											  inputRef,
											  value,
											  onValueChange,
											  drops = "down",
										  }: UseLegacySingleDatePickerOptions) {
	const onValueChangeRef = useRef(onValueChange);
	// Mirrors the committed value for the event handlers, which are bound once (in the setup effect)
	// and would otherwise close over a stale `value`.
	const latestValueRef = useRef(value);

	useEffect(() => {
		onValueChangeRef.current = onValueChange;
	}, [onValueChange]);

	useEffect(() => {
		latestValueRef.current = value;
	}, [value]);

	useEffect(() => {
		const input = inputRef.current;
		if (!input || !hasLegacyDateRangePickerSupport()) {
			return;
		}

		const dateInput = $(input);
		dateInput.daterangepicker({
			singleDatePicker: true,
			opens: "right",
			drops,
			autoUpdateInput: false,
			locale: {
				format: globals.dateFormat,
				monthNames: globals.monthNames,
				daysOfWeek: globals.dayNamesShort,
				firstDay: globals.firstDayOfWeek,
			},
		});

		const onShow = (event: JQuery.TriggeredEvent, picker: DateRangePicker.Picker) => {
			const inputElement = event.target as HTMLInputElement;
			const pickerValue = picker.startDate.format(globals.dateFormat);
			if (inputElement.value !== pickerValue) {
				picker.elementChanged();
			}

			dateInput.off("keydown.reactSingleDatePicker");
			dateInput.on("keydown.reactSingleDatePicker", (keyEvent) => {
				if (keyEvent.keyCode === 27) {
					picker.hide();
				}
			});
		};

		const onApply = (_event: JQuery.TriggeredEvent, picker: DateRangePicker.Picker) => {
			// picker.startDate is already a moment; convert it to the canonical Temporal date.
			onValueChangeRef.current(momentToPlainDate(picker.startDate));
		};

		const onHide = (event: JQuery.TriggeredEvent, picker: DateRangePicker.Picker) => {
			const eventInput = $(event.target);
			const typed = parseDateInput(eventInput.val() as string);
			if (typed) {
				// Keyboard entry does not fire the `apply` event, so a typed value is only seen here on
				// hide. Commit it — but only when it actually changed, so merely opening and closing the
				// picker does not re-emit (which would re-run consumers like a dependent end-date shift).
				if (!isSameDay(typed, latestValueRef.current)) {
					onValueChangeRef.current(typed);
				}
				return;
			}
			// Otherwise the typed text is invalid: revert the display to the picker's date and emit it.
			eventInput.val(picker.startDate.format(picker.locale.format));
			onValueChangeRef.current(momentToPlainDate(picker.startDate));
		};

		dateInput.on("show.daterangepicker.reactSingleDatePicker", onShow);
		dateInput.on("apply.daterangepicker.reactSingleDatePicker", onApply);
		dateInput.on("hide.daterangepicker.reactSingleDatePicker", onHide);

		return () => {
			dateInput.off(".reactSingleDatePicker");
			const picker = dateInput.data("daterangepicker");
			picker?.remove?.();
		};
	}, [inputRef, drops]);

	useEffect(() => {
		const input = inputRef.current;
		if (!input) {
			return;
		}

		// The input element's text is the only place the formatted string still exists.
		const text = formatDate(value);
		if (input.value !== text) {
			input.value = text;
		}

		if (!hasLegacyDateRangePickerSupport()) {
			return;
		}

		const picker = $(input).data("daterangepicker");
		if (!picker) {
			return;
		}

		if (value) {
			// Skip if the picker is already on this day to avoid redundant widget churn.
			if (!isSameDay(momentToPlainDate(picker.startDate), value)) {
				// moment(PlainDate) throws (Temporal poisons valueOf); parse the ISO string instead.
				const momentValue = moment(value.toString(), "YYYY-MM-DD");
				picker.setStartDate(momentValue);
				picker.setEndDate(momentValue);
			}
		}
	}, [inputRef, value]);
}
